import React, { useState } from 'react'
import { Plus, Building2, Eye, Edit, Trash2, Star, MoreHorizontal } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Pagination,
} from '../components/ui'
import { LoadingTable } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useOrganizations } from '../hooks/organizations'
import type { Organization, OrganizationFilters } from '../types/organization.types'
import { OrganizationForm } from '../components/organizations/OrganizationForm'
import { DeleteOrganizationDialog } from '../components/organizations/DeleteOrganizationDialog'
import { OrganizationCard } from '../components/organizations/OrganizationCard'

export function Organizations() {
  const [filters, setFilters] = useState<OrganizationFilters>({
    page: 1,
    limit: 20,
    sortBy: 'name',
    sortOrder: 'asc',
  })

  // Modal states
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isViewModalOpen, setIsViewModalOpen] = useState(false)
  const [selectedOrganization, setSelectedOrganization] = useState<Organization | null>(null)

  const { data, isLoading, error, refetch } = useOrganizations(filters)

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }))
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
  }

  // Action handlers
  const handleView = (organization: Organization) => {
    setSelectedOrganization(organization)
    setIsViewModalOpen(true)
  }

  const handleEdit = (organization: Organization) => {
    setSelectedOrganization(organization)
    setIsEditModalOpen(true)
  }

  const handleDelete = (organization: Organization) => {
    setSelectedOrganization(organization)
    setIsDeleteModalOpen(true)
  }

  const getSourceBadge = (organization: Organization) => {
    if (organization.code_1c) {
      return <Badge variant="secondary">1С</Badge>
    }
    return <Badge variant="outline">TCO</Badge>
  }

  const getStatusBadge = (organization: Organization) => {
    if (organization.is_active) {
      return <Badge variant="secondary" className="bg-green-100 text-green-800">Активна</Badge>
    }
    return <Badge variant="secondary" className="bg-red-100 text-red-800">Неактивна</Badge>
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Организации</h1>
          <p className="text-muted-foreground">
            Управление юридическими лицами холдинга
          </p>
        </div>
        <ErrorState
          title="Ошибка загрузки организаций"
          onAction={refetch}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Организации</h1>
          <p className="text-muted-foreground">
            Управление юридическими лицами холдинга
          </p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Добавить организацию
        </Button>
      </div>

      {/* Statistics */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Building2 className="h-5 w-5 text-muted-foreground" />
                <div>
                  <p className="text-2xl font-bold">{data.total}</p>
                  <p className="text-sm text-muted-foreground">Всего организаций</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Star className="h-5 w-5 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">
                    {data.data.filter(o => o.is_headquarters).length}
                  </p>
                  <p className="text-sm text-muted-foreground">Головных</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="mr-1 bg-green-100">Активных</Badge>
                <div>
                  <p className="text-2xl font-bold">
                    {data.data.filter(o => o.is_active).length}
                  </p>
                  <p className="text-sm text-muted-foreground">организаций</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center space-x-2">
                <Badge variant="secondary" className="mr-1">1С</Badge>
                <div>
                  <p className="text-2xl font-bold">
                    {data.data.filter(o => o.code_1c).length}
                  </p>
                  <p className="text-sm text-muted-foreground">Из 1С</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>Список организаций</CardTitle>
          <CardDescription>
            {data && `Показано ${data.data.length} из ${data.total} организаций`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingTable rows={filters.limit || 20} />
          ) : data && data.data.length > 0 ? (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Название</TableHead>
                    <TableHead>БИН</TableHead>
                    <TableHead>Тип</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Источник</TableHead>
                    <TableHead>Дата создания</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.data.map((organization) => (
                    <TableRow key={organization.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{organization.name}</p>
                            {organization.is_headquarters && (
                              <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            )}
                          </div>
                          {organization.legal_name && (
                            <p className="text-sm text-muted-foreground">
                              {organization.legal_name}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm">{organization.bin}</span>
                      </TableCell>
                      <TableCell>
                        {organization.is_headquarters ? (
                          <Badge variant="default">Головная</Badge>
                        ) : (
                          <Badge variant="outline">Дочерняя</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(organization)}
                      </TableCell>
                      <TableCell>
                        {getSourceBadge(organization)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {formatDate(organization.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleView(organization)}
                            title="Просмотр"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(organization)}
                            title="Редактировать"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(organization)}
                            title="Удалить"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" title="Дополнительные действия">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <div className="flex justify-center">
                  <Pagination
                    currentPage={data.page}
                    totalPages={data.totalPages}
                    onPageChange={handlePageChange}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
              <h3 className="mt-2 text-sm font-semibold text-muted-foreground">
                Организации не найдены
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Попробуйте изменить фильтры поиска или добавьте новую организацию.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <OrganizationForm
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        mode="create"
      />

      <OrganizationForm
        open={isEditModalOpen}
        onOpenChange={setIsEditModalOpen}
        organization={selectedOrganization}
        mode="edit"
      />

      <DeleteOrganizationDialog
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        organization={selectedOrganization}
      />

      <OrganizationCard
        open={isViewModalOpen}
        onOpenChange={setIsViewModalOpen}
        organization={selectedOrganization}
      />
    </div>
  )
}
